import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { applyServerErrors, CheckboxField, FormErrorSummary, InstitutionField, MoneyField, SelectField, zOptionalMoney, zRequiredString } from '@/business';
import { Button, Drawer, DrawerContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';

import { useAddSettlementItem } from '../../api/mutations';
import { lookupsQuery } from '../../api/queries';

const FACILITIES = ['personal_finance', 'credit_card', 'auto_finance', 'home_finance', 'overdraft', 'other'] as const;
const schema = z.object({ bankId: zRequiredString(), facilityType: z.enum(FACILITIES), liabilityAmount: zOptionalMoney({ min: '1.00' }), isMain: z.boolean() });
type Values = z.infer<typeof schema>;

/** Adds a bank whose liability is settled by this deal (main or secondary). */
export function SettlementItemDrawer({ dealId, bankCode, firstItem, onClose }: { dealId: string; bankCode: string; firstItem: boolean; onClose: () => void }) {
  const { t } = useTranslation('deals');
  const lookups = useQuery(lookupsQuery);
  const add = useAddSettlementItem(dealId);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { bankId: '', facilityType: 'personal_finance', liabilityAmount: null, isMain: firstItem } });
  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await add.mutateAsync({ bankId: v.bankId, facilityType: v.facilityType, isMain: v.isMain, ...(v.liabilityAmount ? { liabilityAmount: v.liabilityAmount } : {}) });
      toast.done(t('settlement.added'));
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, ['bankId', 'liabilityAmount']);
        setProblem(e);
      }
    }
  });
  // Any institution of the bank master except the deal's own bank (B1).
  const institutions = (lookups.data?.financialInstitutions ?? []).filter((b) => b.code !== bankCode);
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <DrawerContent
        title={t('settlement.addTitle')}
        description={t('settlement.addBody')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={add.isPending} onClick={() => void submit()}>
              {t('settlement.add')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4 px-5 py-4">
          {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
          <InstitutionField control={form.control} name="bankId" label={t('settlement.bank')} institutions={institutions} />
          <SelectField control={form.control} name="facilityType" label={t('settlement.facility')} options={FACILITIES.map((f) => ({ value: f, label: t(`workflow.settlement.facility.${f}`, { ns: 'common' }) }))} />
          <MoneyField control={form.control} name="liabilityAmount" label={t('settlement.liability')} help={t('settlement.liabilityHelp')} optional />
          <CheckboxField control={form.control} name="isMain" label={t('settlement.isMain')} />
          <button type="submit" hidden />
        </form>
      </DrawerContent>
    </Drawer>
  );
}
